import Vue from 'vue';
import axios from 'axios';
import config from '@/config/constants.js';
import {firebase, firestore} from '@/config/firebase';
import $router from '@/router/index.js';
// import initCaptcha from '@/config/captcha';
import initCaptcha, { clearCaptcha, getCaptchaInstance } from '@/config/captcha';
import { startPresence, stopPresence, markActive, markAway } from '@/util/presence';

export default {
  namespaced: true,
  state: {
    status: '',
    // token: localStorage.getItem(config.token_key) || '',
    phone_number: '',
    user: JSON.parse(localStorage.getItem(config.token_key)) || '',
    token: localStorage.getItem('auth_token') || '',
    role: '',
    // Live snapshot of the calling user's CURRENT tenant doc. Populated
    // by Web.vue's tenant doc listener (runs for every role, not just
    // owners). Used by tenant_billing_state + paymentPermission getters
    // so non-owners (agent admins, vendors, employees) react to the
    // tenant being cancel-scheduled or hard-cancelled even though their
    // own user.billing_active flag may not have updated yet.
    tenant: {
      billing_active: null,
      billing_status: null,
      cancel_scheduled_at: null,
      cancel_effective_at: null,
      canceled_at: null,
      company_name: null,
      owner_id: null,
    },
    modal: false,
    options: {
      tenants_list: []
    },
    user_tmp: null,
    appleStoreUrl: process.env.VUE_APP_APPLE_STORE_URL,
    googlePlayUrl: process.env.VUE_APP_GOOGLE_PLAY_URL,
    deleting_status: "",
    options_company: [],
    options_agents: [],
    options_vendors: [],
    options_users: [],
    total_company: 0,
    total_agents: 0,
    total_vendors: 0,
    total_users: 0,
    total_agents_active: 0,
    total_agents_inactive: 0,
    total_vendors_active: 0,
    total_vendors_inactive: 0,
    total_users_active: 0,
    total_users_inactive: 0,
    agents_by_status: {},
    vendors_by_status: {},
    clients_by_status: {},
  },
  mutations: {
    // Patches the in-memory user with the given fields. Uses Vue.set so
    // keys that don't yet exist on state.user (e.g. the first time an
    // owner accepts the Free Tier Partner Agreement and we write
    // free_terms_accepted_version) become reactive — a plain
    // `state.user[i] = ...` assignment is NOT reactive for new keys in
    // Vue 2, which left computeds like Web.vue's should_show_free_tier_modal
    // stale until a full page reload. Unlike updateTenant we deliberately
    // keep the SAME object reference (no Object.assign) so Web.vue's
    // `user` watcher doesn't fire and tear down / recreate the Firestore
    // listeners on every snapshot commit.
    updateUser(state, data) {
      if (!state.user || typeof state.user !== 'object') return;
      for (const i in data) {
        Vue.set(state.user, i, data[i]);
      }
      localStorage.setItem(config.token_key, JSON.stringify(state.user));
    },
    // Live tenant doc snapshot. Replacing the object reference (vs in-
    // place property assignment) guarantees Vue 2 reactivity even when
    // new keys (cancel_scheduled_at, etc.) appear for the first time.
    updateTenant(state, data) {
      state.tenant = Object.assign({}, state.tenant || {}, data || {});
    },
    async set_options(state, data) {
      state.options = data;
    },
    async user_tmp(state, data) {
      state.user_tmp = data;
    },
    state_modal(state, status) {
      state.modal = status;
    },
    loading(state) {
      state.status = 'loading';
    },
    status(state, status) {
      state.status = status;
    },
    set_phone(state, phone) {
      state.phone_number = phone;
    },

    auth_success(state, data) {
      state.status = 'auth_success';
      state.user = data.user;
      state.token = data.access_token;
      localStorage.setItem('auth_token', state.token);
      localStorage.setItem(config.token_key, JSON.stringify(state.user));
      axios.defaults.headers.common['Authorization'] = state.token;
    },
    auth_update(state, user) {
      state.user.first_name = user.first_name;
      state.user.last_name = user.last_name;
      state.user.company_name = user.company_name;
      state.user.service = user.service;
      state.user.selected_tags = user.selected_tags;
      state.user.tag_line = user.tag_line;
      state.user.profile_picture = user.profile_picture;

      localStorage.setItem(config.token_key, JSON.stringify(state.user));
    },
    auth_test(state, user) {
      state.status = 'auth_success';
      state.user = user;
    },
    auth_error(state) {
      state.status = 'auth_error';
    },
    safety_logout(state) {
      state.status = '';
      state.user = '';
      state.token = '';
      state.tenant = {
        billing_active: null,
        billing_status: null,
        cancel_scheduled_at: null,
        cancel_effective_at: null,
        canceled_at: null,
        company_name: null,
        owner_id: null,
      };
      localStorage.removeItem('auth_token');
      localStorage.removeItem(config.token_key);
    },
    logout(state) {
      state.status = '';
      state.user = '';
      state.token = '';
      // Clear tenant doc snapshot so CancellationBanner /
      // NavPaymentBanner / paymentPermission don't bleed into the
      // /login page after a cancelled-tenant user signs out.
      state.tenant = {
        billing_active: null,
        billing_status: null,
        cancel_scheduled_at: null,
        cancel_effective_at: null,
        canceled_at: null,
        company_name: null,
        owner_id: null,
      };
      localStorage.removeItem('auth_token');
      localStorage.removeItem(config.token_key);
      if (window.confirmationResult) {
        window.confirmationResult = null;
      }

      // Hard redirect — $router.push silently failed for some users
      // (probably an in-flight navigation guard intercepting). A full
      // reload guarantees we land on /login with a clean app state,
      // and also forces Firestore listeners + Stripe iframes from the
      // logged-out tenant to release before the next session.
      try { $router.push('/login').catch(() => {}); } catch (_) { /* noop */ }
      window.location.href = '/login';
    },
    user(state, user) {
      state.user = user;
      localStorage.setItem(config.token_key, JSON.stringify(state.user));
    },
    deleting_status(state, payload) {
      state.deleting_status = payload;
    },
    set_option_companies(state, data) {
      state.options_company = data;
      state.total_company = data.length;
    },
    set_option_agents(state, data) {
      state.options_agents = data;
      state.total_agents = data.length;
    },
    set_option_vendors(state, data) {
      state.options_vendors = data;
      state.total_vendors = data.length;
    },
    set_option_users(state, data) {
      state.options_users = data;
      state.total_users = data.length;
    },
    set_total_agents_active(state, data) {
      state.total_agents_active = data;
    },
    set_total_agents_inactive(state, data) {
      state.total_agents_inactive = data;
    },
    set_total_vendors_active(state, data) {
      state.total_vendors_active = data;
    },
    set_total_vendors_inactive(state, data) {
      state.total_vendors_inactive = data;
    },
    set_total_users_active(state, data) {
      state.total_users_active = data;
    },
    set_total_users_inactive(state, data) {
      state.total_users_inactive = data;
    },
    set_agents_by_status(state, data) {
      state.agents_by_status = data;
    },
    set_vendors_by_status(state, data) {
      state.vendors_by_status = data;
    },
    set_clients_by_status(state, data) {
      state.clients_by_status = data;
    },
  },
  actions: {
    delete_old_account({commit}, tenant) {
      commit("deleting_status", "loading");
      return new Promise((resolve, reject) => {
        axios
          .post(config.api_url + 'user/me/delete_old_account', {
            tenant_id: tenant.tenant_id,
            role: tenant.role,
            owner_id: tenant.owner_id
          })
          .then((response) => {
            commit("deleting_status", "complete");
            resolve(response.data);
          })
          .catch((error) => {
            commit("deleting_status", "complete");
            reject(error.data);
          });
      })
    },
    async send_sms({ commit }, phone_number) {
      commit('loading');

      let back_phone = phone_number;
      let cleanPhone = phone_number.replaceAll(' ', '').replaceAll('-', '');
      let phoneWithExtension = config.phone_extension + cleanPhone;

      commit('set_phone', phone_number);

      try {
        // Reuse existing reCAPTCHA verifier (reset only if expired)
        const expired = getCaptchaInstance()?._expired;
        const { auth, recaptcha } = initCaptcha(expired || false);

        // Verifica si el teléfono existe via API (no Firestore directo)
        const checkResponse = await axios.post(
            config.api_url + 'auth/check_client',
            { phone_number: phoneWithExtension }
        );

        if (!checkResponse.data.exists) {
          commit('status', 'sms_not_sent');
          throw new Error('The cell phone number is not registered on KIEY');
        }

        try {
          const confirmationResult = await auth.signInWithPhoneNumber(phoneWithExtension, recaptcha);
          window.confirmationResult = confirmationResult;
        } catch (captchaError) {
          // reCAPTCHA failed — reset and retry once with a fresh verifier
          if (captchaError.code === 'auth/captcha-check-failed' ||
              captchaError.code === 'auth/argument-error' ||
              captchaError.code === 'auth/internal-error') {
            console.warn('[send_sms] reCAPTCHA failed, retrying with fresh verifier:', captchaError.code);
            const { auth: retryAuth, recaptcha: retryRecaptcha } = initCaptcha(true);
            const confirmationResult = await retryAuth.signInWithPhoneNumber(phoneWithExtension, retryRecaptcha);
            window.confirmationResult = confirmationResult;
          } else {
            throw captchaError;
          }
        }

        commit('status', 'sms_sent');
        commit('set_phone', back_phone);

        return 'success';
      } catch (error) {
        if (error.message !== 'The cell phone number is not registered on KIEY') {
          commit('status', '');
        }
        // Surface friendly message for network errors
        if (error.code === 'unavailable' || error.code === 'auth/network-request-failed') {
          throw new Error('No internet connection — check your network and try again');
        }
        if (error.code === 'auth/too-many-requests') {
          throw new Error('Too many attempts — please wait a few minutes and try again');
        }
        throw error;
      }
    },
    async checkIfClient({ commit, dispatch }, data) {
      commit('loading');

      try {
        let phone = data['phone_number']
            .replaceAll(' ', '')
            .replaceAll('-', '');

        const fullPhone = config.phone_extension + phone;

        // Obtener Firebase ID token para autenticación
        let headers = {};
        const currentUser = firebase.auth().currentUser;
        if (currentUser) {
          const firebaseIdToken = await currentUser.getIdToken();
          headers['X-Firebase-Token'] = firebaseIdToken;
        }

        // Llamar al endpoint autenticado para obtener tenants
        const response = await axios.post(
            config.api_url + 'auth/get_tenants',
            { phone_number: fullPhone, user_id: fullPhone },
            { headers }
        );

        const responseData = response.data.data;

        if (!responseData || !responseData.user_id || responseData.tenants.length === 0) {
          commit('status', '');
          return 'NO_ADMIN';
        }

        const tenants = responseData.tenants;
        let haveSomeAccountActive = false;
        let haveSomeTenantBillingActive = false;

        // Reconstruir user object para set_options (compatibilidad con el resto del flujo)
        const tenantsList = tenants.map(t => ({
          tenant_id: t.tenant_id,
          role: t.role,
          status: t.status,
          company_name: t.company_name,
        }));

        const user = {
          id: responseData.user_id,
          owner_id: responseData.user_id,
          phone_number: fullPhone,
          tenants_list: tenantsList,
          is_system_super_user: responseData.is_system_super_user || false,
        };

        for (const tenant of tenants) {
          const tStatus = (tenant.status || '').toLowerCase();
          if (!tenant.status || tStatus === "active") {
            haveSomeAccountActive = tenant;
          }
          if (tenant.billing_active === true || tenant.billing_active === undefined) {
            haveSomeTenantBillingActive = true;
          }
        }

        // set_options usando datos del API en vez de Firestore
        await dispatch('set_options_from_api', { user, tenants });

        // Sub-users without owner / vendor / super_user role (agent admins,
        // employee admins) historically got NO_BILLING when their parent
        // tenant was cancelled, which loops the login back to phone entry
        // with "Your account is inactive." That blocks the cancelled-
        // tenant lockout UX (banner + read-only /owner/account + Data
        // Export) — sub-users in a cancelled tenant need to reach those
        // pages at minimum to export their data. Let admin sub-users
        // through the gate; the route guard reroutes them into the
        // lockout view. Only fall back to NO_ACTIVE / NO_BILLING when
        // the user has NO admin foothold at all.
        if (!tenantsList.find(x => ["owner", "vendor", "super_user"].includes(x.role))) {
          const hasAdminFoothold = tenants.some(t =>
            t.is_admin && (!t.status || (t.status || '').toLowerCase() === 'active')
          );

          commit('status', '');

          if (!hasAdminFoothold) {
            if (!haveSomeAccountActive) {
              return "NO_ACTIVE";
            }
            if (!haveSomeTenantBillingActive) {
              return "NO_BILLING";
            }
          }
        }

        let isClient =
            tenantsList.find(x => x.role === "client") &&
            tenantsList.length === 1;

        if (isClient) {
          commit('status', '');
          return "NO_ADMIN";
        } else {
          let isOwner = tenantsList.find(x => x.role === "owner");
          let isVendor = tenantsList.find(x => x.role === "vendor");
          let isSuperUser = tenantsList.find(x => x.role === "super_user");

          if (!isOwner && !isVendor && !isSuperUser) {
            let isAdmin = tenants.some(t =>
                (!t.status || (t.status || '').toLowerCase() === 'active') && t.is_admin
            );

            commit('status', '');
            return isAdmin ? false : "NO_ADMIN";
          } else {
            commit('status', '');
            return false;
          }
        }
      } catch (error) {
        console.error('[checkIfClient] error:', error);
        commit('status', '');
        throw error;
      }
    },
    async verify_sms_code({ commit }, code) {
      commit('loading');

      if (!window.confirmationResult) {
        commit('auth_error');
        throw new Error('No confirmation result. Please request a new SMS.');
      }

      let user;

      try {
        const credential = firebase.auth.PhoneAuthProvider.credential(
            window.confirmationResult.verificationId,
            code
        );
        const result = await firebase.auth().signInWithCredential(credential);
        user = result.user;
      } catch (error) {
        commit('auth_error');
        throw new Error('Invalid verification code');
      }

      commit('status', '');
      return user;
    },
    activate_ut: async function ({ commit, dispatch, state }, data) {

      commit('state_modal', false);
      // If we're already tracking presence, this is a tenant-switch: stamp
      // "away" on the tenant we're leaving (using the still-current token)
      // before the new token takes over below. No-op on a fresh login.
      try { markAway(); } catch (e) { /* noop */ }
      try {

        // Get Firebase ID token for server-side verification
        let headers = {};
        const currentUser = firebase.auth().currentUser;
        if (currentUser) {
          const firebaseIdToken = await currentUser.getIdToken();
          headers['X-Firebase-Token'] = firebaseIdToken;
        }

        const response = await axios.post(
            config.api_url + 'login',
            data,
            { headers }
        );

        if (!response.data.user) {
          commit('auth_error');
          $router.push('/login');

          throw new Error('User not found');
        }

        const user = response.data.user;
        commit('auth_success', response.data);

        // The new session token is now active (axios default + localStorage).
        // Start tracking presence (idempotent) and mark active now — on a
        // tenant-switch this flips us back to "Active now" on the new tenant.
        try { startPresence(); markActive(); } catch (e) { /* noop */ }

        // Wipe every tenant-scoped cache so a previous tenant's agents /
        // vendors / clients can't leak into this session. This is the
        // chokepoint for super-admin alias switches AND the regular
        // tenant-switch modal, so clearing here covers both paths.
        try {
          dispatch('Users_list/clear_data', { fullReset: true }, { root: true });
          dispatch('Vendors_list/clear_data', null, { root: true });
          commit('Chat/conversations', [], { root: true });
          commit('Chat/files', [], { root: true });
          commit('Chat/setUxDocs', {}, { root: true });
          localStorage.removeItem('conversations');
          localStorage.removeItem('uxDocs');
        } catch (e) {
          console.warn('[activate_ut] tenant cache clear skipped:', e);
        }

        // -----------------
        // ROLE ROUTING
        // -----------------

        if (
            user.user_role === 'vendor' ||
            (user.user_role === 'employee' && user.is_admin)
        ) {

          $router.push('/vendor/vendor-admin-panel');

        } else if (
            user.user_role === 'owner' ||
            (user.user_role === 'agent' && user.is_admin) ||
            user.user_role === 'super_user'
        ) {

          // Post-signup add_users/add_vendors flow has been removed; owners
          // and agent admins land directly on the admin panel after login.
          $router.push('/owner/owner-admin-panel');

        } else {

          commit('auth_error');

          $router.push({
            path: '/login',
            query: {
              phone_number: state.phone_number,
              is_admin: "no-admin"
            }
          });

          throw new Error(
              'You do not have access to KIEY on the web. Please download the app.'
          );
        }

        return user;

      } catch (err) {

        console.error('[activate_ut] ERROR:', err);
        commit('auth_error');

        // Distinguish network errors from server/auth errors
        if (!err.response && err.message === 'Network Error') {
          throw 'No internet connection — check your network and try again';
        }
        if (err.code === 'ECONNABORTED') {
          throw 'Connection timed out — please try again';
        }

        throw err?.response?.data?.message ||
        err.message ||
        'Login failed';
      }
    },
    // set_options: async ({commit}, data) => {
    //   // eslint-disable-next-line no-async-promise-executor
    //   return new Promise(async (resolve) => {
    //     let newOptions = [];
    //     commit("user_tmp", data);
    //     let tenant_ids = [];
    //
    //     for (const tenant of data['tenants_list']) {
    //       tenant_ids.push(tenant.tenant_id);
    //     }
    //
    //     let size = 10;
    //     let tenant_ids_split = [];
    //     for (let i = 0; i < tenant_ids.length; i += size) {
    //       tenant_ids_split.push(tenant_ids.slice(i, i + size));
    //     }
    //
    //     let subUsersRef;
    //     try {
    //       subUsersRef = await firestore
    //           .collectionGroup("users")
    //           .where("owner_id", "==", data.owner_id)
    //           .get();
    //     } catch (error) {
    //       console.error("Error fetching subusers:", error);
    //       subUsersRef = null;
    //     }
    //
    //     let subusers = {};
    //     if (subUsersRef) {
    //       for (const subuser of subUsersRef.docs) {
    //         subusers[subuser.data().tenant_id] = {
    //           ...subuser.data(),
    //           id: subuser.id
    //         };
    //       }
    //     }
    //
    //     for (const tenant_ids of tenant_ids_split) {
    //       const tenantsRef = await firestore
    //         .collection("tenants")
    //         .where("id", "in", tenant_ids)
    //         .get().catch(error => console.log(error));
    //       let tenant = {};
    //       for (const tenantRef of tenantsRef.docs) {
    //         let haveSomeTenantBillingActive = false;
    //         const subuser = subusers[tenantRef.id];
    //         if (data.owner_id !== tenantRef.data().owner_id) {
    //           if (subuser) {
    //             tenant = data['tenants_list'].find(x => x.tenant_id === tenantRef.id && x.role === subuser.user_role);
    //             if (
    //                 subuser.disabled_by_agent_owner === true ||
    //                 subuser.is_active === false
    //             ) {
    //               continue;
    //             }
    //           }
    //         } else {
    //           tenant = data['tenants_list'].find(x => x.tenant_id === tenantRef.id && x.role === "owner");
    //         }
    //         if (tenantRef.exists) {
    //           if (tenantRef.data().billing_active === true || tenantRef.data().billing_active === undefined) {
    //             haveSomeTenantBillingActive = true;
    //           } else if (subuser && subuser.user_role === "vendor") {
    //             haveSomeTenantBillingActive = true;
    //           } else if (data.owner_id === tenantRef.data().owner_id) {
    //             haveSomeTenantBillingActive = true;
    //           }
    //         }
    //         let canAddTenant = false;
    //         if (["agent", "employee"].includes(tenant.role) && tenant.status === "inactive") {
    //           canAddTenant = false;
    //         } else if (haveSomeTenantBillingActive && (tenant.status === undefined || tenant.status !== 'inactive') && tenant.role !== "client") {
    //           canAddTenant = true;
    //         } else if (haveSomeTenantBillingActive && ["owner", "vendor"].includes(tenant.role) && tenant.role !== "client") {
    //           canAddTenant = true;
    //         } else if (["super_user"].includes(tenant.role)) {
    //           canAddTenant = true;
    //         }
    //
    //         if (canAddTenant) {
    //           if (!newOptions.find(x => x.tenant_id === tenant.tenant_id)) {
    //             newOptions.push(tenant);
    //           }
    //         }
    //       }
    //     }
    //
    //     let formatted = {
    //       ...data,
    //       tenants_list: newOptions
    //     };
    //     commit("set_options", formatted);
    //     resolve();
    //   });
    // },
    // Usado por checkIfClient con datos del API get_tenants (sin Firestore directo)
    set_options_from_api: async ({ commit }, { user, tenants }) => {
      commit("user_tmp", user);

      let newOptions = [];

      for (const tenant of tenants) {
        const tenantEntry = user.tenants_list.find(
            t => t.tenant_id === tenant.tenant_id
        );
        if (!tenantEntry) continue;

        // Verificar si puede agregar
        if (tenant.disabled_by_agent_owner || tenant.is_active === false) continue;

        let haveBilling = tenant.billing_active === true || tenant.billing_active === undefined;

        let canAddTenant = false;
        if (["super_user"].includes(tenantEntry.role)) {
          canAddTenant = true;
        } else if (
            ["agent", "employee"].includes(tenantEntry.role) &&
            tenantEntry.status === "inactive"
        ) {
          canAddTenant = false;
        } else if (
            haveBilling &&
            (tenantEntry.status === undefined || tenantEntry.status !== "inactive") &&
            tenantEntry.role !== "client"
        ) {
          canAddTenant = true;
        } else if (
            haveBilling &&
            ["owner", "vendor"].includes(tenantEntry.role) &&
            tenantEntry.role !== "client"
        ) {
          canAddTenant = true;
        } else if (
            // Cancelled-tenant lockout: admin sub-users need to reach
            // the read-only /owner/account view + Data Export. The
            // route guard handles the actual lockout UX once they're
            // logged in.
            !haveBilling &&
            tenant.is_admin === true &&
            tenantEntry.status !== "inactive" &&
            ["agent", "employee", "owner", "vendor"].includes(tenantEntry.role)
        ) {
          canAddTenant = true;
        }

        if (canAddTenant && !newOptions.find(x => x.tenant_id === tenantEntry.tenant_id)) {
          newOptions.push(tenantEntry);
        }
      }

      const formatted = {
        ...user,
        tenants_list: newOptions
      };
      commit("set_options", formatted);
    },
    // Usado por get_options (super user, post-auth) — mantiene queries Firestore directas
    set_options: async ({ commit }, data) => {

      let newOptions = [];
      commit("user_tmp", data);

      // Recolectar tenant_ids
      let tenant_ids = data.tenants_list.map(t => t.tenant_id || t.id);
      tenant_ids = [...new Set(tenant_ids)]; // evitar duplicados

      // Dividir en bloques de 10
      let size = 10;
      let tenant_ids_split = [];
      for (let i = 0; i < tenant_ids.length; i += size) {
        tenant_ids_split.push(tenant_ids.slice(i, i + size));
      }

      // Run subuser queries and tenant queries in parallel
      const subUserPromises = tenant_ids.map(tid =>
        firestore
          .collection("tenants").doc(tid).collection("users")
          .where("owner_id", "==", data.owner_id)
          .get()
          .catch(error => {
            console.error("[set_options] Error fetching subusers for tenant " + tid + ":", error);
            return null;
          })
      );

      const tenantBlockPromises = tenant_ids_split.map(block =>
        firestore
          .collection("tenants")
          .where("id", "in", block)
          .get()
          .catch(error => {
            console.error("[set_options] tenants query error:", error);
            return null;
          })
      );

      // Wait for both in parallel
      const [subUserResults, tenantBlockResults] = await Promise.all([
        Promise.all(subUserPromises),
        Promise.all(tenantBlockPromises)
      ]);

      // Process subusers
      let subusers = {};
      for (const subUserSnap of subUserResults) {
        if (subUserSnap) {
          for (const doc of subUserSnap.docs) {
            subusers[doc.data().tenant_id] = {
              ...doc.data(),
              id: doc.id
            };
          }
        }
      }

      // Función auxiliar para fallback seguro
      const getTenantFallback = (tenantObj, tenantRef, defaultRole) => {
        if (tenantObj) return tenantObj;
        return {
          tenant_id: tenantRef.id,
          role: defaultRole,
          company_name: tenantRef.data().company_name,
          status: tenantRef.data().status || 'active'
        };
      };

      // Process tenant blocks (already fetched in parallel)
      let haveSomeTenantBillingActive = false;
      for (const tenantsRef of tenantBlockResults) {
        if (!tenantsRef) continue;
        for (const tenantRef of tenantsRef.docs) {
          let tenant;

          const subuser = subusers[tenantRef.id];

          if (data.owner_id !== tenantRef.data().owner_id && subuser) {
            tenant = data.tenants_list.find(
                x => x.tenant_id === tenantRef.id && x.role === subuser.user_role
            );
            tenant = getTenantFallback(tenant, tenantRef, subuser.user_role);

            if (subuser.disabled_by_agent_owner || subuser.is_active === false) continue;

          } else {
            const role = tenantRef.data().role || "owner";
            tenant = data.tenants_list.find(
                x => x.tenant_id === tenantRef.id && x.role === role
            );
            tenant = getTenantFallback(tenant, tenantRef, role);
          }

          // Track billing status for checkIfClient
          let tenantBillingActive = false;
          if (
              tenantRef.data().billing_active === true ||
              tenantRef.data().billing_active === undefined ||
              (subuser && subuser.user_role === "vendor") ||
              data.owner_id === tenantRef.data().owner_id
          ) {
            tenantBillingActive = true;
            haveSomeTenantBillingActive = true;
          }

          let canAddTenant = false;
          if (["super_user"].includes(tenant.role)) {
            canAddTenant = true;
          } else if (
              ["agent", "employee"].includes(tenant.role) &&
              tenant.status === "inactive"
          ) {
            canAddTenant = false;
          } else if (
              tenantBillingActive &&
              (tenant.status === undefined || tenant.status !== "inactive") &&
              tenant.role !== "client"
          ) {
            canAddTenant = true;
          } else if (
              tenantBillingActive &&
              ["owner", "vendor"].includes(tenant.role) &&
              tenant.role !== "client"
          ) {
            canAddTenant = true;
          }

          if (canAddTenant && !newOptions.find(x => x.tenant_id === tenant.tenant_id)) {
            newOptions.push(tenant);
          }
        }
      }

      const formatted = {
        ...data,
        tenants_list: newOptions
      };
      commit("set_options", formatted);

      // Return metadata for checkIfClient to avoid duplicate queries
      return { haveSomeTenantBillingActive, subusers };
    },
    activate: function ({ commit, dispatch, state }, data) {
      data['phone_number'] = data['phone_number']
          .replaceAll(' ', '')
          .replaceAll('-', '');

      data['phone_number'] = config.phone_extension + data['phone_number'];

      return new Promise(function (resolve, reject) {
        commit('loading');

        if (state.options.tenants_list.length > 1) {
          commit('state_modal', true);
          commit('status', '');
          resolve('multiple_tenants');

        } else if (state.options.tenants_list.length === 1) {
          dispatch('activate_ut', {
            user_id: state.options.phone_number,
            tenant_id: state.options.tenants_list[0].tenant_id
          })
              .then(res => {
                resolve(res);
              })
              .catch(error => {
                commit('status', '');
                reject(error);
              });

        } else {
          commit('status', '');
          reject('There is no tenant account active');
        }
      });
    },
    logout({commit, dispatch}) {
      // Stamp "away" before the token is cleared so the user doesn't linger as
      // "Active now" after signing out, then stop tracking.
      try { markAway(); stopPresence(); } catch (e) { /* noop */ }
      dispatch('Call/cleanup', null, { root: true });
      dispatch('Users_list/clear_data', { fullReset: true }, { root: true });
      dispatch('Chat/simple_logout', null, { root: true });
      dispatch('Vendors_list/clear_data', null, { root: true });
      clearCaptcha();
      commit('logout');
    },

    // Audit Item #6 — must mirror `logout`'s dispatch sequence. Without
    // these, the super-admin "return to super user" flow leaves the
    // previous tenant's Chat / Users_list / Vendors_list / Call state
    // in memory, and the next tenant's session inherits it (the long-
    // standing "data from previous tenant" bug). The MUTATION stays
    // unchanged on purpose — it deliberately does NOT hard-reload to
    // /login the way `logout` does, so the user stays in-app while
    // they're returning to a super-user identity.
    safety_logout({commit, dispatch}) {
      // Mirror logout: stamp "away" + stop tracking before the token is cleared.
      try { markAway(); stopPresence(); } catch (e) { /* noop */ }
      dispatch('Call/cleanup', null, { root: true });
      dispatch('Users_list/clear_data', { fullReset: true }, { root: true });
      dispatch('Chat/simple_logout', null, { root: true });
      dispatch('Vendors_list/clear_data', null, { root: true });
      clearCaptcha();
      commit('safety_logout');
    },

    get_options: async function ({commit}) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve) => {
        let usersRef = await firestore.collection("users").get();

        let users_owner = [];
        let users_owner_phone_number = [];
        let users_company = [];
        let users_tenant_list = [];
        for (const user of usersRef.docs) {
          users_owner[user.id] = user.data().first_name + ' ' + user.data().last_name;
          users_owner_phone_number[user.id] = user.data().phone_number;
          users_company[user.id] = user.data().company_name;
          users_tenant_list[user.id] = user.data().tenants_list;
        }
        let tenantsRef = await firestore.collection('tenants').get();
        let optionCompanies = [];
        let optionAgents = [];
        let agentsInserted = [];
        let optionVendors = [];
        let vendorsInserted = [];
        let optionUsers = [];

        let agentsActive = 0;
        let agentsInactive = 0;
        let vendorsActive = 0;
        let vendorsInactive = 0;
        let usersActive = 0;
        let usersInactive = 0;
        const agentsByStatus = {};
        const vendorsByStatus = {};
        const clientsByStatus = {};
        const bump = (bucket, status) => {
          const key = status || 'Unknown';
          bucket[key] = (bucket[key] || 0) + 1;
        };


        // Fetch all tenant users in parallel instead of sequential N+1 queries
        const tenantUserResults = await Promise.all(
          tenantsRef.docs.map(tenant =>
            firestore.collection("tenants").doc(tenant.id).collection("users").get()
              .then(snap => ({ tenantId: tenant.id, tenantData: tenant.data(), docs: snap.docs }))
          )
        );

        for (const { tenantId, tenantData, docs } of tenantUserResults) {
          const tenant = { id: tenantId, data: () => tenantData };
          let agentsRef = { docs };

          let actives = 0;
          let inactives = 0;
          for (const user of agentsRef.docs) {

            if (user.data().user_role === 'agent') {
              if (!agentsInserted.includes(tenant.id + '-agent' + '-' + users_owner_phone_number[user.data().owner_id])) {
                let agent_company_name = '';
                if (users_tenant_list[user.data().owner_id] !== undefined) {
                  for (const company of users_tenant_list[user.data().owner_id]) {
                    if (company.tenant_id === tenant.id) {
                      agent_company_name = company.company_name
                    }
                  }
                  let status_user = user.data().status || 'Unknown';
                  optionAgents.push({
                    tenant_id: tenant.id + '-agent' + '-' + users_owner_phone_number[user.data().owner_id],
                    company_name: users_owner[user.data().owner_id] + ' (' + agent_company_name + ') ' + ' (' + status_user + ')'
                  });
                  agentsInserted.push(tenant.id + '-agent' + '-' + users_owner_phone_number[user.data().owner_id]);
                  bump(agentsByStatus, status_user);
                  if (status_user === 'Active') {
                    agentsActive++;
                    actives++;
                  } else {
                    agentsInactive++;
                    inactives++;
                  }
                }
              }
            }
            if (user.data().user_role === 'vendor') {
              if (!vendorsInserted.includes(tenant.id + '-vendor' + '-' + users_owner_phone_number[user.data().owner_id])) {
                let agent_company_name = '';
                if (users_tenant_list[user.data().owner_id] !== undefined) {
                  for (const company of users_tenant_list[user.data().owner_id]) {
                    if (company.tenant_id === tenant.id) {
                      agent_company_name = company.company_name
                    }
                  }
                  let status_user = user.data().status || 'Unknown';
                  optionVendors.push({
                    tenant_id: tenant.id + '-vendor' + '-' + users_owner_phone_number[user.data().owner_id],
                    company_name: users_owner[user.data().owner_id] + '(' + users_company[user.data().owner_id] + ', ' + agent_company_name + ') ' + ' (' + status_user + ')'
                  });
                  vendorsInserted.push(tenant.id + '-vendor' + '-' + users_owner_phone_number[user.data().owner_id]);
                  bump(vendorsByStatus, status_user);
                  if (status_user === 'Active') {
                    vendorsActive++;
                    actives++;
                  } else {
                    vendorsInactive++;
                    inactives++;
                  }
                }
              }
            }
            if (user.data().user_role === 'employee') {
              if (!vendorsInserted.includes(tenant.id + '-employee' + '-' + users_owner_phone_number[user.data().vendor_id])) {
                if (users_owner_phone_number[user.data().vendor_id] !== undefined) {
                  if (users_tenant_list[user.data().owner_id] !== undefined) {
                    let agent_company_name = '';
                    for (const company of users_tenant_list[user.data().owner_id]) {
                      if (company.tenant_id === tenant.id) {
                        agent_company_name = company.company_name
                      }
                    }
                    let status_user = user.data().status || 'Unknown';
                    optionVendors.push({
                      tenant_id: tenant.id + '-employee' + '-' + users_owner_phone_number[user.data().vendor_id],
                      company_name: users_owner[user.data().owner_id] + ' (' + users_company[user.data().vendor_id] + ', ' + agent_company_name + ') ' + ' (' + status_user + ')'
                    });
                    vendorsInserted.push(tenant.id + '-employee' + '-' + users_owner_phone_number[user.data().vendor_id]);
                    bump(vendorsByStatus, status_user);
                    if (status_user === 'Active') {
                      vendorsActive++;
                      actives++;
                    } else {
                      vendorsInactive++;
                      inactives++;
                    }
                  }
                }
              }
            }
            if (user.data().user_role !== undefined && user.data().user_role === 'client') {
              if (users_owner[user.data().owner_id] !== undefined) {
                let status_user = user.data().status || 'Unknown';
                optionUsers.push({
                  owner_id: user.data().owner_id + '-' + tenant.id + '-' + 'agent' + '-' + users_owner_phone_number[tenant.data().owner_id],
                  tenant_id: tenant.id,
                  company_name: users_owner[user.data().owner_id] + ' (' + users_owner[user.data().invited_by_agent] + ', ' + user.data().company_name + ') ' + ' (' + status_user + ')'
                });
                bump(clientsByStatus, status_user);
                if (status_user === 'Active') {
                  usersActive++;
                  actives++
                } else {
                  usersInactive++;
                  inactives++;
                }
              }
            }
          }
          let total_users_for_tenant = actives + inactives;
          optionCompanies.push({
            tenant_id: tenant.id + '-' + tenant.data().user_role + '-' + users_owner_phone_number[tenant.data().owner_id],
            company_name: tenant.data().company_name + '(' + total_users_for_tenant + ' users)' + '(Active ' + actives + ')' + '(Inactive ' + inactives + ')'
          });
        }
        //ordering sort options
        optionCompanies.sort((a, b) => {
          if (a.company_name.toLowerCase() < b.company_name.toLowerCase()) {
            return -1;
          }
          if (a.company_name.toLowerCase() > b.company_name.toLowerCase()) {
            return 1;
          }
          return 0;
        });

        optionAgents.sort((a, b) => {
          if (a.company_name.toLowerCase() < b.company_name.toLowerCase()) {
            return -1;
          }
          if (a.company_name.toLowerCase() > b.company_name.toLowerCase()) {
            return 1;
          }
          return 0;
        });

        optionVendors.sort((a, b) => {
          if (a.company_name.toLowerCase() < b.company_name.toLowerCase()) {
            return -1;
          }
          if (a.company_name.toLowerCase() > b.company_name.toLowerCase()) {
            return 1;
          }
          return 0;
        });

        optionUsers.sort((a, b) => {
          if (a.company_name < b.company_name) {
            return -1;
          }
          if (a.company_name > b.company_name) {
            return 1;
          }
          return 0;
        });


        // optionAgents.unshift({
        //     tenant_id: tenantsRef.docs[0].id+ '-super_user-'+ config.phone_extension + state.phone_number.replaceAll('-', ''),
        //     company_name: 'ALL'
        // });

        commit('set_option_companies', optionCompanies);
        commit('set_option_agents', optionAgents);
        commit('set_option_vendors', optionVendors);
        commit('set_option_users', optionUsers);

        commit('set_total_agents_active', agentsActive);
        commit('set_total_agents_inactive', agentsInactive);
        commit('set_total_vendors_active', vendorsActive);
        commit('set_total_vendors_inactive', vendorsInactive);
        commit('set_total_users_active', usersActive);
        commit('set_total_users_inactive', usersInactive);
        commit('set_agents_by_status', agentsByStatus);
        commit('set_vendors_by_status', vendorsByStatus);
        commit('set_clients_by_status', clientsByStatus);
        resolve();
      });
    },

    disableEmailNotifications: function ({commit}, owner_id) {
      let docRef = firestore.collection("users").doc(owner_id);

      docRef.get().then((doc) => {
        if (doc.exists) {
          firestore.collection("users").doc(owner_id).set({email_notifications: false}, {merge: true})
        }
      }).catch((error) => {
        console.error(error);
      });
      commit('status', '');

    },
  },
  getters: {
    getAppleStoreUrl(state) {
      return state.appleStoreUrl;
    },
    getGooglePlayUrl(state) {
      return state.googlePlayUrl;
    },
    isLoggedIn(state) {
      return state.token != '' && !!state.user && typeof state.user === 'object';
    },
    userRole(state) {
      if (state.user['user_role'] === 'super_user') {
        return 'owner';
      }
      return state.user['role'] || state.user['user_role'];
    },
    current_user(state) {
      return state.user;
    },
    // Returns false when the calling user is locked out by billing.
    // Old behavior: only owners and vendors with their own
    // user.billing_active=false were gated. NEW: every user in a tenant
    // whose tenant doc has billing_active === false is also gated, so
    // agent admins / employees / vendors-in-a-cancelled-owner-tenant
    // can no longer bypass the cancellation by virtue of their role.
    paymentPermission(state) {
      if (state.user.billing_active === false && ['owner', 'vendor'].includes(state.user.user_role)) return false;
      if (state.tenant && state.tenant.billing_active === false) return false;
      return true;
    },
    accountEnabledPermission(state) {
      return !(state.user.disabled_by_agent_owner === true && ['vendor'].includes(state.user.user_role))
    },
    // 3-state classifier driven off the live tenant doc snapshot. UI
    // banner + lockout routing read this. Falls back to 'active' if the
    // tenant listener hasn't populated yet so we don't flash a banner
    // pre-load.
    tenant_billing_state(state) {
      const t = state.tenant || {};
      if (t.billing_active === false) return 'cancelled';
      if (t.billing_status === 'pending_cancel' || (t.cancel_scheduled_at && t.cancel_effective_at && Date.now() < t.cancel_effective_at)) {
        return 'pending_cancel';
      }
      return 'active';
    },
    tenant_cancel_effective_at(state) {
      return (state.tenant && state.tenant.cancel_effective_at) || null;
    },
    tenant_owner_id(state) {
      return (state.tenant && state.tenant.owner_id) || null;
    },
    // True when the calling user can act on behalf of the tenant — see
    // the Reactivate flow, manage billing, etc. False for non-admin
    // sub-users (regular agents, employees, vendors-as-sub-users) who
    // can only see the cancellation notice + Data Export.
    //
    // Priority of evidence:
    //   1. super_user — always admin everywhere
    //   2. user.owner_id === tenant.owner_id — strongest signal that
    //      the calling auth identity IS the tenant owner. Avoids the
    //      lag bug where a switch-account leaves user_role stale for
    //      the new tenant.
    //   3. user_role === 'agent' && is_admin — agent admins per Tyler
    //      spec get the same gate as owner.
    //   4. user_role === 'owner' as fallback — covers the brief window
    //      between login and the tenant doc listener populating
    //      state.tenant. Without it the post-login UI would flicker
    //      into 'non-admin' for real owners.
    is_tenant_admin(state) {
      const u = state.user || {};
      const t = state.tenant || {};
      if (u.user_role === 'super_user') return true;
      if (u.owner_id && t.owner_id && u.owner_id === t.owner_id) return true;
      if (u.user_role === 'agent' && u.is_admin === true) return true;
      // Fallback for pre-listener load. If tenant.owner_id is known
      // and DOESN'T match this user, drop the role-only shortcut —
      // they're clearly a sub-user in this tenant.
      if (u.user_role === 'owner' && (!t.owner_id || t.owner_id === u.owner_id)) return true;
      return false;
    },
  },
  modules: {},
};
